import { CellData, Appointment, Vehicle } from '../API/APIObjects';
import { GMImageFallback } from '../CommonControls/Image/ImageFallback';
import { ColumnType } from '../CommonControls/Table';
import { Locales } from '../Contexts/Localization';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import css from './Formatter.module.scss';
import { formatVersionNumber } from './Version';
import { GoMotoFunction } from '../API/GoMoto.types';

const dateFormatterOptions12hour: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
} as const;
const dateFormatterOptions24hour: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
} as const;
const dateOnlyFormatter: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
} as const;
/*
 * 'shortGeneric' yields the region abbreviation without the DST variant (CT rather than CDT/CST),
 * which is what gets pasted into troubleshooting write-ups alongside a provider's own log.
 */
const timestampFormatterOptions12hour: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
    timeZoneName: 'shortGeneric',
} as const;
const timestampFormatterOptions24hour: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
    timeZoneName: 'shortGeneric',
} as const;

const enDateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.en, dateFormatterOptions12hour);
const esDateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.es, dateFormatterOptions12hour);
const frDateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.fr, dateFormatterOptions24hour);
const enDateOnlyFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.en, dateOnlyFormatter);
const esDateOnlyFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.es, dateOnlyFormatter);
const frDateOnlyFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.fr, dateOnlyFormatter);
const enTimestampFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.en, timestampFormatterOptions12hour);
const esTimestampFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.es, timestampFormatterOptions12hour);
const frTimestampFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(Locales.fr, timestampFormatterOptions24hour);

/**
 * Formats date object to readable string
 *
 * @param date the date to format
 * @param locale the optional locale to use
 */
export const formatDate = (date: Date | string | null | undefined, locale: Locales = Locales.en) => {
    if (!date || new Date(date)
        .toString() === 'Invalid Date') {
        return '';
    }

    switch (locale) {
        case Locales.en:
            return enDateFormatter.format(new Date(date));
        case Locales.es:
            return esDateFormatter.format(new Date(date));
        case Locales.fr:
            return frDateFormatter.format(new Date(date));
        default:
            return '';
    }
};

/**
 * Formats date object to readable string with no time part
 *
 * @param date the date to format
 * @param locale the optional locale to use; defaults to `Locales.en`
 */
export const formatDateOnly = (date: Date | string | null | undefined, locale: Locales = Locales.en) => {
    if (!date || new Date(date)
        .toString() === 'Invalid Date') {
        return '';
    }

    switch (locale) {
        case Locales.en:
            return enDateOnlyFormatter.format(new Date(date));
        case Locales.es:
            return esDateOnlyFormatter.format(new Date(date));
        case Locales.fr:
            return frDateOnlyFormatter.format(new Date(date));
        default:
            return '';
    }
};

/**
 * Formats date object to readable string carrying seconds and the reader's timezone abbreviation.
 *
 * Prefer {@link formatDate} for ordinary display; this precision exists for logs where the order of
 * two events within the same minute matters and the reading has to be compared against another system.
 *
 * @param date the date to format
 * @param locale the optional locale to use; defaults to `Locales.en`
 */
export const formatTimestampWithZone = (date: Date | string | null | undefined, locale: Locales = Locales.en) => {
    if (!date || new Date(date)
        .toString() === 'Invalid Date') {
        return '';
    }

    switch (locale) {
        case Locales.en:
            return enTimestampFormatter.format(new Date(date));
        case Locales.es:
            return esTimestampFormatter.format(new Date(date));
        case Locales.fr:
            return frTimestampFormatter.format(new Date(date));
        default:
            return '';
    }
};

/**
 * Formats phone number to (###) ###-####
 */
export const formatPhoneNumber = (phone: string) => {
    let cleaned: string = ('' + phone).replace('+1', '')
        .replace(/\D/g, '');
    let match: RegExpMatchArray | null = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match !== null && match.length > 3) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return '';
};

/**
 * Formats a decimal value into a percentage string with a precision of at most 2
 */
export const formatDecimalAsPercentage = (value: number | string) => {
    if (typeof value === 'string') {
        value = parseFloat(value);
    }

    if (isNaN(value)) {
        return '';
    }

    return ((value * 100).toFixed(2) + '%').replace('.00', '')
        .replace('.0', '');
};

/**
 * Formats a value into currency
 */
export const formatAsCurrency = (value: number | string, locale: string = 'en-US', currency = 'USD') => {
    if (typeof value === 'string') {
        /*
         * Assume value is already formatted
         * TODO localization
         */
        if (value.includes('$')) {
            return value;
        }

        value = parseFloat(value);
    }

    if (isNaN(value)) {
        value = 0;
    }

    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
    })
        .format(value);
};

/**
 * Formats the name based on whether the first and last name are present
 */
export const formatName = (firstName: string | null, lastName: string | null, username: string, lastfirst: boolean = true, firstNameAsFallback = false) => {
    const first: string = formatNameCapitalization(firstName ?? '');
    const last: string = formatNameCapitalization(lastName ?? '');

    if (last && first) {
        if (lastfirst) {
            return `${last}, ${first}`;
        }
        else {
            return `${first} ${last}`;
        }
    }
    else if (last) {
        return last;
    }
    else if (first && firstNameAsFallback) {
        return first;
    }
    else if (username) {
        return username;
    }

    return '';
};

/**
 * Formats the name based on whether the first and last name are present
 */
export const formatUserInitials = (firstName: string | null, lastName: string | null, username: string) => {
    let name: string = '';

    if (firstName && lastName) {
        name = firstName[0] + lastName[0];
    }
    else if (lastName) {
        name = lastName[0];
    }
    else if (firstName) {
        name = firstName[0];
    }
    else {
        name = username[0];
    }

    return name;
};

/**
 * Formats the name of an individual or business with standard capitalization rules
 */
export const formatNameCapitalization = (value: string = '', isBusinessName: boolean = false): string => {
    let formatted;
    if (isBusinessName !== true) {
        formatted = (value ?? '')
            .trim()
            .toLowerCase()
            // remove multiple spaces
            .replace(/\s+/g, ' ')
            // standard spacing after commas
            .replace(/\s*,\s*/g, ', ')
            // anna-maria -> Anna-Maria
            .replace(/(\p{L})(-|\/)(\p{L})/gu, (_, a, b, c) => `${a.toLowerCase()}${b}${c.toUpperCase()}`)
            // e.g. mcgraw -> McGraw, macdonald -> MacDonald
            .replace(/\bm(a?c)(\p{L})/gu, (_, a, b) => `M${a}${b.toUpperCase()}`)
            // e.g. o'connor -> O'Connor, xi'an -> Xi'An (> 1 letter following apostrophe or dash)
            .replace(/\b(\p{L})(\p{L}*['-])(\p{L})(?=\p{L})/gu, (_, a, b, c) => `${a.toUpperCase()}${b}${c.toUpperCase()}`)
            // e.g. mark's -> Mark's, james' -> James' (0 or 1 letter following apostrophe)
            .replace(/\b(\p{L})(\p{L}+'\p{L}?)(?=\s|\b|$)/gu, (_, a, b) => `${a.toUpperCase()}${b}`)
            // capitalize first letter of terms with only letters
            .replace(/(?<=\s|^)(\p{L})(?=\p{L}*(?:\s|\.|,|$))/gu, (m) => m.toUpperCase());
    }
    else {
        formatted = (value ?? '')
            .trim()
            .toLowerCase()
            // remove multiple spaces
            .replace(/\s+/g, ' ')
            // standard spacing after commas
            .replace(/\s*,\s*/g, ', ')
            .replace(/\s*,\s*/g, ', ')
            // e.g. a-action -> A-Action, a/c -> A/C, grub-n-go -> Grub-N-Go, heckler & kock-attorneys/assoc -> Heckler & Koch-Attorneys/Assoc
            .replace(/((?<=\b)\p{L}{1}(?=[^\s/-]*(?:[-]|[/])))|((?:(?<=[-]|[/]))\p{L}{1})/gu, (m) => m.toUpperCase())
            // e.g. mcgraw -> McGraw, macdonald -> MacDonald
            .replace(/\bm(a?c)(\p{L})/gu, (_, a, b) => `M${a}${b.toUpperCase()}`)
            // e.g. o'connor -> O'Connor, xi'an -> Xi'An (> 1 letter following apostrophe or dash)
            .replace(/\b(\p{L})(\p{L}*['-])(\p{L})(?=\p{L})/gu, (_, a, b, c) => `${a.toUpperCase()}${b}${c.toUpperCase()}`)
            // e.g. mark's -> Mark's, james' -> James' (0 or 1 letter following apostrophe)
            .replace(/\b(\p{L})(\p{L}+'\p{L}?)(?=\s|\b|$)/gu, (_, a, b) => `${a.toUpperCase()}${b}`)
            /*
             * Adds spaces around ampersand when there are more than 2 characters on either side of the ampersand and ampersand has no space on at least one side
             * For example:
             * Cook's A/C&Heating, Inc. -> Cook's A/C & Heating, Inc.
             * Cook's A/C &Heating, Inc. -> Cook's A/C & Heating, Inc.
             * Cook's A/C& Heating, Inc. -> Cook's A/C & Heating, Inc.
             * I want to note it will not add spaces in the case of this example: Cook's A/C & Heating, Inc. -> Cook's A/C & Heating, Inc.
             */
            .replace(/(?<=\b[\p{L}\p{N}\p{S}\p{M}\p{P}]{3,})((?<!\s+)&\s+|(?<!\s+)&(?!\s+)|\s+&(?!\s+))(?=[\p{L}\p{N}\p{S}\p{M}\p{P}]{3,}\b)/gu, ' & ')
            // capitalize first letter of terms with only letters
            .replace(/(?<=\s|^)(\p{L})(?=\p{L}*(?:\s|\.|,|$))/gu, (m) => m.toUpperCase())
            /*
             * capitalize letters that precede an ampersand for businesses such as: AT&T, 3M&C, etc.
             * e.g. At&T -> AT&T, 3m&C -> 3M&C
             */
            .replace(/(?<![\p{L}\p{N}\p{S}\p{P}\p{M}]{1,})([\p{L}\p{N}]{1,2})(?=\s*&\s*(?=[\p{L}\p{N}]{1,2}(?![\p{S}\p{P}\p{M}])\b))/gu, (m) => m.toUpperCase())
            /*
             * capitalize letters that trail an ampersand for businesses such as: AT&T, 3M&C, etc.
             * e.g. AT&Tt -> AT&TT, A&mM -> A&MM
             */
            .replace(/(?<=(?<=\b(?<![\p{S}\p{P}\p{M}])[\p{L}\p{N}]{1,2})\s*&\s*)([\p{L}]{1,2})(?![\p{S}\p{P}\p{M}\p{L}\p{N}]{1,})/gu, (m) => m.toUpperCase())
            /*
             * Removes spaces around ampersand when there are at most 2 letters on either side of the ampersand
             * e.g. AT & T -> AT&T, M &M -> M&M, A& M -> A&M, 3M & C -> 3M&C
             */
            .replace(/(?<=\b(?<![\p{S}\p{P}\p{M}])[\p{L}\p{N}]{1,2})(\s*&\s+|\s+&\s*)(?=[\p{L}\p{N}]{1,2}(?![\p{S}\p{P}\p{M}])\b)/gu, '&');
    }

    // 1 and 2-letter terms in all uppercase if ampersand present (i.e. business name with ampersand)
    return formatted.includes('&') ? formatted.replace(/(?<=\s|^)(\p{L}{1,2})(?=\s|,|$)/gu, (m) => m.toUpperCase()) : formatted;
};

/**
 * Formats an appointment name for display for both individuals and businesses
 */
export const formatAppointmentName = (appointment: Pick<Appointment, 'lastName' | 'firstName' | 'businessName' | 'towInCompanyName'>, firstThenLast: boolean = false): string => {
    const last = (appointment.lastName ?? '').trim();
    const first = (appointment.firstName ?? '').trim();
    const business = (appointment.businessName ?? '').trim();

    if (appointment.towInCompanyName)
        return last;
    else if (business !== '')
        return formatNameCapitalization(business, true);
    else if (first === '' && last !== '')
        return formatNameCapitalization(last, true);
    else if (last !== '' && first !== '')
        return firstThenLast ? `${formatNameCapitalization(first)} ${formatNameCapitalization(last)}` : `${formatNameCapitalization(last)}, ${formatNameCapitalization(first)}`;
    else
        return formatNameCapitalization(last !== '' ? last : first);
};

/**
 * Formats a vehicle name for display
 *
 * @param vehicle the vehicle or appointment with a vehicle to format
 * @param locale the optional locale to use
 */
export const formatVehicleName: GoMotoFunction<[Vehicle | Appointment, Locales], string> = (vehicle, locale = Locales.en) => {
    const year: string = vehicle.year || '';
    const make: string = ((vehicle.chromeMake ?? '').trim() === '' ? formatNameCapitalization(vehicle.make) : vehicle.chromeMake) || '';
    const model: string = ((vehicle.chromeModel ?? '').trim() === '' ? formatNameCapitalization(vehicle.model) : vehicle.chromeModel) || '';

    return (locale === Locales.fr ? [make, model, year] : [year, make, model]).map((i) => i.trim())
        .filter((i) => i !== '')
        .join(' ');
};

/**
 *
 * @param cellValue Cell data to format
 * @param colType Column type to determin how to format cell data
 * @param replaceWithIfEmpty Data to display in cell if the cell data is an empty string, null, or undefined
 * @param isExport Whether to format for an export
 * @param key Key used for rendered JSX elements when the formatted data is a JSX element.
 */
export const formatTableData = (
    cellValue: CellData,
    colType: ColumnType = ColumnType.Normal,
    replaceWithIfEmpty: CellData = undefined,
    isExport: boolean = false,
    key: string = '',
    locale: Locales = Locales.en,
) => {
    if (cellValue !== '' && cellValue !== null && cellValue !== undefined) {
        switch (colType) {
            case ColumnType.Date:
                return formatDate(new Date(cellValue.toString()), locale);
            case ColumnType.Boolean:
                return cellValue ? 'Y' : null;
            case ColumnType.BooleanYN:
                return cellValue ? 'Y' : 'N';
            case ColumnType.BooleanCheck:
                return cellValue ?
                    isExport ? 'Y' :
                        <div className={css.icon}>
                            <CheckIcon />
                        </div>
                    : null;
            case ColumnType.BooleanString:
                if (cellValue.toLowerCase() === 'true') {
                    return isExport ? cellValue :
                        <div className={css.icon}>
                            <CheckIcon />
                        </div>;
                }
                else if (cellValue.toLowerCase() === 'false') {
                    return isExport ? cellValue :
                        <div className={css.icon}>
                            <CloseIcon />
                        </div>;
                }
                else {
                    return null;
                }
            case ColumnType.Phone:
                return formatPhoneNumber(cellValue.toString());
            case ColumnType.List:
                return cellValue.join(isExport ? '|' : ', ');
            case ColumnType.ListMultiLine:
                return isExport ? cellValue.join('|') :
                    cellValue.map((value: any) => (
                        <>
                            {value}
                            <br />
                        </>
                    ));
            case ColumnType.Image:
                return isExport ? '' :
                    <div className={css.tableDataImage}>
                        <GMImageFallback
                            altText={''}
                            src={cellValue}
                            key={`${key}-${cellValue}`}
                        />
                    </div>;
            case ColumnType.VersionNumber:
                return formatVersionNumber(cellValue);
            case ColumnType.Currency:
                return formatAsCurrency(cellValue, locale);
            case ColumnType.Percent:
                return formatDecimalAsPercentage(cellValue);
            case ColumnType.IndividualName:
                return formatNameCapitalization(cellValue);
        }
    }
    else if (replaceWithIfEmpty !== undefined) {
        return replaceWithIfEmpty;
    }

    return cellValue;
};